import { get, push, ref, remove, set, update } from 'firebase/database';
import { deleteObject, getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { db, storage } from '../config/firebase';

const nowIso = () => new Date().toISOString();

const operatorPayload = (operator) => ({
  uid: operator?.uid || null,
  name:
    operator?.firstName || operator?.lastName
      ? `${operator?.firstName || ''} ${operator?.lastName || ''}`.trim()
      : operator?.displayName || operator?.email || 'Unknown',
});

const slugify = (value) =>
  String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

export const generateBranchSlug = (name, existingSlugs = []) => {
  const base = slugify(name) || 'branch';
  if (!existingSlugs.includes(base)) return base;
  let suffix = 2;
  while (existingSlugs.includes(`${base}-${suffix}`)) suffix += 1;
  return `${base}-${suffix}`;
};

export const saveAcademyProfile = async ({ profile, operator }) => {
  const now = nowIso();
  const payload = {
    name: profile.name || '',
    shortName: profile.shortName || '',
    registrationNumber: profile.registrationNumber || '',
    logoURL: profile.logoURL || '',
    contactEmail: profile.contactEmail || '',
    contactPhone: profile.contactPhone || '',
    website: profile.website || '',
    addressLine: profile.addressLine || '',
    tvecRegistrations: profile.tvecRegistrations || {},
    tagline: profile.tagline || '',
    updatedAt: now,
    updatedBy: operatorPayload(operator),
  };
  await update(ref(db, 'settings/academy'), payload);
  return payload;
};

export const uploadAcademyLogo = async ({ file }) => {
  if (!file) throw new Error('No logo file provided.');
  const extension = (file.name || '').split('.').pop() || 'png';
  const path = `settings/academy/logo-${Date.now()}.${extension}`;
  const fileRef = storageRef(storage, path);
  await uploadBytes(fileRef, file);
  const logoURL = await getDownloadURL(fileRef);
  return { logoURL, path };
};

export const deleteAcademyLogo = async ({ path }) => {
  if (!path) return;
  try {
    await deleteObject(storageRef(storage, path));
  } catch (err) {
    if (err?.code !== 'storage/object-not-found') {
      console.warn('Failed to delete old academy logo:', err);
    }
  }
};

export const seedDefaultBranches = async ({ operator }) => {
  const snapshot = await get(ref(db, 'settings/branches'));
  const existing = snapshot.val() || {};
  if (Object.keys(existing).length) return { seeded: false, branches: existing };

  const now = nowIso();
  const op = operatorPayload(operator);
  const seed = [
    {
      name: 'Colombo',
      slug: 'colombo',
      city: 'Colombo',
      addressLine: '',
      contactPhone: '',
      contactEmail: '',
      googleMapsUrl: '',
      isActive: true,
      order: 0,
      createdAt: now,
      createdBy: op,
    },
    {
      name: 'Kandy',
      slug: 'kandy',
      city: 'Kandy',
      addressLine: '',
      contactPhone: '',
      contactEmail: '',
      googleMapsUrl: '',
      isActive: true,
      order: 1,
      createdAt: now,
      createdBy: op,
    },
  ];

  const branches = {};
  for (const entry of seed) {
    const branchRef = push(ref(db, 'settings/branches'));
    const payload = { ...entry, id: branchRef.key };
    await set(branchRef, payload);
    branches[branchRef.key] = payload;
  }

  return { seeded: true, branches };
};

export const upsertBranch = async ({ branchId, values, operator }) => {
  const existingSnapshot = await get(ref(db, 'settings/branches'));
  const existing = existingSnapshot.val() || {};
  const existingSlugs = Object.entries(existing)
    .filter(([id]) => id !== branchId)
    .map(([, value]) => value?.slug)
    .filter(Boolean);

  const now = nowIso();
  const op = operatorPayload(operator);

  const preparedSlug =
    values.slug && values.slug.trim()
      ? slugify(values.slug)
      : generateBranchSlug(values.name, existingSlugs);

  const payload = {
    name: (values.name || '').trim(),
    slug: preparedSlug,
    city: (values.city || '').trim(),
    addressLine: (values.addressLine || '').trim(),
    contactPhone: (values.contactPhone || '').trim(),
    contactEmail: (values.contactEmail || '').trim(),
    googleMapsUrl: (values.googleMapsUrl || '').trim(),
    isActive: values.isActive !== false,
    order: Number.isFinite(values.order) ? values.order : 0,
    updatedAt: now,
    updatedBy: op,
  };

  if (branchId) {
    const updates = { ...payload, id: branchId };
    await update(ref(db, `settings/branches/${branchId}`), updates);
    return { ...existing[branchId], ...updates };
  }

  const branchRef = push(ref(db, 'settings/branches'));
  const created = {
    ...payload,
    id: branchRef.key,
    createdAt: now,
    createdBy: op,
  };
  await set(branchRef, created);
  return created;
};

export const deleteBranch = async ({ branchId }) => {
  if (!branchId) return;
  await remove(ref(db, `settings/branches/${branchId}`));
};

export const migrateBatchesToBranchIds = async ({ branches }) => {
  if (!branches || !Object.keys(branches).length) {
    throw new Error('Seed branches before migrating batches.');
  }
  const branchList = Object.values(branches);
  const bySlug = branchList.reduce((acc, branch) => {
    if (branch?.slug) acc[branch.slug] = branch;
    return acc;
  }, {});
  const byId = branchList.reduce((acc, branch) => {
    if (branch?.id) acc[branch.id] = branch;
    return acc;
  }, {});

  const coursesSnapshot = await get(ref(db, 'courses'));
  const courses = coursesSnapshot.val() || {};
  const updates = {};
  let updatedBatches = 0;
  let scannedBatches = 0;

  Object.entries(courses).forEach(([courseId, course]) => {
    const batches = Array.isArray(course?.batches) ? course.batches : [];
    if (!batches.length) return;

    let courseChanged = false;
    const migrated = batches.map((batch) => {
      scannedBatches += 1;
      if (!batch) return batch;
      if (batch.branchId && byId[batch.branchId]) return batch;

      const legacy = String(batch.branch || '').toLowerCase().trim();
      const match =
        (batch.branchId && byId[batch.branchId]) ||
        bySlug[legacy] ||
        branchList.find(
          (branch) =>
            String(branch?.name || '').toLowerCase() === legacy ||
            String(branch?.slug || '').toLowerCase() === legacy,
        ) ||
        null;

      if (!match) return batch;
      courseChanged = true;
      updatedBatches += 1;
      return {
        ...batch,
        branchId: match.id,
        branchName: match.name,
        branch: match.slug || legacy,
      };
    });

    if (courseChanged) {
      updates[`courses/${courseId}/batches`] = migrated;
    }
  });

  if (Object.keys(updates).length) {
    updates['settings/branches_meta/migratedAt'] = nowIso();
    await update(ref(db), updates);
  } else {
    await update(ref(db, 'settings/branches_meta'), { migratedAt: nowIso() });
  }

  return { scannedBatches, updatedBatches };
};

export const upsertPaymentMethod = async ({ methodId, values, operator }) => {
  const now = nowIso();
  const op = operatorPayload(operator);

  const payload = {
    type: values.type,
    label: (values.label || '').trim(),
    enabled: values.enabled !== false,
    order: Number.isFinite(values.order) ? values.order : 0,
    availableFor: Array.isArray(values.availableFor)
      ? values.availableFor.filter(Boolean)
      : [],
    details: values.details && typeof values.details === 'object' ? values.details : {},
    instructions: values.instructions || '',
    updatedAt: now,
    updatedBy: op,
  };

  if (!payload.type) throw new Error('Payment method type is required.');
  if (!payload.label) throw new Error('Payment method label is required.');
  if (!payload.availableFor.length) {
    throw new Error('Select at least one audience for this payment method.');
  }

  if (methodId) {
    await update(ref(db, `settings/paymentMethods/${methodId}`), {
      ...payload,
      id: methodId,
    });
    return { ...payload, id: methodId };
  }

  const methodRef = push(ref(db, 'settings/paymentMethods'));
  const created = {
    ...payload,
    id: methodRef.key,
    createdAt: now,
    createdBy: op,
  };
  await set(methodRef, created);
  return created;
};

export const deletePaymentMethod = async ({ methodId }) => {
  if (!methodId) return;
  await remove(ref(db, `settings/paymentMethods/${methodId}`));
};

export const setPaymentMethodEnabled = async ({ methodId, enabled, operator }) => {
  if (!methodId) return;
  await update(ref(db, `settings/paymentMethods/${methodId}`), {
    enabled: Boolean(enabled),
    updatedAt: nowIso(),
    updatedBy: operatorPayload(operator),
  });
};
